import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Check, Clock, Timer, Play, CalendarClock,
  ArrowUp, AlertCircle
} from 'lucide-react';

const TIMER_TYPES = [
  {
    id: 'countdown_duration',
    name: 'Countdown',
    desc: 'Count down from a set duration',
    icon: Timer,
    example: '05:00 → 00:00'
  },
  {
    id: 'countdown_datetime',
    name: 'Countdown to Date',
    desc: 'Count down to a specific date/time',
    icon: CalendarClock,
    example: 'Dec 31, 11:59 PM'
  },
  {
    id: 'countup',
    name: 'Count Up',
    desc: 'Count up from zero',
    icon: ArrowUp,
    example: '00:00 → ∞'
  },
];

const SIZE_OPTIONS = [
  { id: 'small', name: 'Small', desc: 'Corner overlay' },
  { id: 'medium', name: 'Medium', desc: 'Inline display' },
  { id: 'large', name: 'Large', desc: 'Centered focus' },
];

const POSITION_OPTIONS = [
  { id: 'top_left', name: 'Top Left' },
  { id: 'top_right', name: 'Top Right' },
  { id: 'bottom_left', name: 'Bottom Left' },
  { id: 'bottom_right', name: 'Bottom Right' },
  { id: 'center', name: 'Center' },
];

const FORMAT_OPTIONS = [
  { id: 'MM_SS', name: 'MM:SS', example: '05:30' },
  { id: 'HH_MM_SS', name: 'HH:MM:SS', example: '01:05:30' },
  { id: 'D_HH_MM_SS', name: 'Xd HH:MM:SS', example: '2d 01:05:30' },
];

const ACTION_ON_ZERO = [
  { id: 'stop', name: 'Stop at 00:00', desc: 'Timer stays visible at zero' },
  { id: 'hide', name: 'Hide automatically', desc: 'Timer disappears when done' },
  { id: 'flash', name: 'Flash alert', desc: 'Visual pulse for 3-5 seconds' },
];

const DEFAULT_THEME = {
  backgroundColor: '#6a1fbf',
  textColor: '#ffffff',
  borderColor: '#ffffff',
  transparentBackground: false,
};

// Custom Select component - uses portal to avoid overflow clipping
const Select = ({ value, onValueChange, children, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current && !selectRef.current.contains(event.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(event.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !selectRef.current) return;
    const updatePosition = () => {
      const rect = selectRef.current.getBoundingClientRect();
      const dropdownHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow;
      setDropdownStyle({
        position: 'fixed', left: rect.left, width: rect.width, zIndex: 99999,
        ...(openUpward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => { window.removeEventListener('scroll', updatePosition, true); window.removeEventListener('resize', updatePosition); };
  }, [isOpen]);

  const options = React.Children.toArray(children);
  const selectedOption = options.find(opt => opt.props.value === value);

  return (
    <div className="relative" ref={selectRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:border-white/20 transition-colors"
      >
        <span className={selectedOption ? '' : 'text-gray-500'}>
          {selectedOption ? selectedOption.props.children : placeholder}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && ReactDOM.createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.props.value}
              type="button"
              onClick={() => { onValueChange(option.props.value); setIsOpen(false); }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                option.props.value === value ? 'text-cyan-400' : 'text-gray-300'
              }`}
            >
              {option.props.children}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

const SelectItem = ({ value, children }) => null;

// Custom Switch component
const Switch = ({ checked, onCheckedChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    onClick={() => onCheckedChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-[#6a1fbf]' : 'bg-white/20'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

// Tabs components
const Tabs = ({ defaultValue, children, className }) => {
  const [activeTab, setActiveTab] = useState(defaultValue);
  return (
    <div className={className}>
      {React.Children.map(children, child => {
        if (child.type === TabsList) {
          return React.cloneElement(child, { activeTab, setActiveTab });
        }
        if (child.type === TabsContent) {
          return child.props.value === activeTab ? child : null;
        }
        return child;
      })}
    </div>
  );
};

const TabsList = ({ children, className, activeTab, setActiveTab }) => (
  <div className={`flex ${className}`}>
    {React.Children.map(children, child =>
      React.cloneElement(child, { activeTab, setActiveTab })
    )}
  </div>
);

const TabsTrigger = ({ value, children, activeTab, setActiveTab }) => (
  <button
    type="button"
    onClick={() => setActiveTab(value)}
    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors rounded-lg ${
      activeTab === value
        ? 'bg-white/10 text-white'
        : 'text-gray-400 hover:text-white'
    }`}
  >
    {children}
  </button>
);

const TabsContent = ({ children, className }) => (
  <div className={className}>{children}</div>
);

function TimerPreview({ config, time }) {
  const sizeClasses = {
    small: 'text-2xl px-3 py-1.5',
    medium: 'text-4xl px-5 py-3',
    large: 'text-6xl px-8 py-5',
  };

  return (
    <div
      className={`rounded-xl font-mono font-bold ${sizeClasses[config.size]}`}
      style={{
        backgroundColor: config.theme.transparentBackground ? 'transparent' : config.theme.backgroundColor,
        color: config.theme.textColor,
        border: config.theme.transparentBackground ? 'none' : `2px solid ${config.theme.borderColor || config.theme.backgroundColor}`,
        textShadow: config.theme.transparentBackground ? '0 2px 8px rgba(0,0,0,0.8)' : 'none'
      }}
    >
      {config.showLabel && config.labelText && (
        <div className="text-sm font-normal mb-1 opacity-80">{config.labelText}</div>
      )}
      {time}
    </div>
  );
}

export default function TimerElementModal({
  isOpen,
  onClose,
  onSave,
  timer,
  folders,
  currentFolder,
  skipFolderSelect = false,
  readOnly = false,
  onCopyToMyElements
}) {
  const [formData, setFormData] = useState({
    name: '',
    timer_type: 'countdown_duration',
    folder_id: currentFolder?.id || '',
    config: {
      durationSeconds: 300,
      durationMinutes: 5,
      durationSecondsInput: 0,
      targetDate: '',
      targetTime: '',
      targetTimestamp: null,
      size: 'medium',
      position: 'bottom_right',
      showLabel: false,
      labelText: '',
      format: 'MM_SS',
      autoStartOnShow: false,
      loopOnZero: false,
      actionOnZero: 'stop',
      showHours: false,
      theme: { ...DEFAULT_THEME }
    }
  });

  const [error, setError] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize form data only when modal opens, not on every prop change
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      if (timer) {
        const mins = Math.floor((timer.config?.durationSeconds || 300) / 60);
        const secs = (timer.config?.durationSeconds || 300) % 60;

        let targetDate = '';
        let targetTime = '';
        if (timer.config?.targetTimestamp) {
          const dt = new Date(timer.config.targetTimestamp * 1000);
          targetDate = dt.toISOString().split('T')[0];
          targetTime = dt.toTimeString().slice(0, 5);
        }

        setFormData({
          name: timer.name || '',
          timer_type: timer.timer_type || 'countdown_duration',
          folder_id: timer.folder_id || currentFolder?.id || '',
          config: {
            ...formData.config,
            ...timer.config,
            durationMinutes: mins,
            durationSecondsInput: secs,
            targetDate,
            targetTime,
            theme: { ...DEFAULT_THEME, ...timer.config?.theme }
          }
        });
      } else {
        setFormData({
          name: '',
          timer_type: 'countdown_duration',
          folder_id: currentFolder?.id || '',
          config: {
            durationSeconds: 300,
            durationMinutes: 5,
            durationSecondsInput: 0,
            targetDate: '',
            targetTime: '',
            targetTimestamp: null,
            size: 'medium',
            position: 'bottom_right',
            showLabel: false,
            labelText: '',
            format: 'MM_SS',
            autoStartOnShow: false,
            loopOnZero: false,
            actionOnZero: 'stop',
            showHours: false,
            theme: { ...DEFAULT_THEME }
          }
        });
      }
      setError('');
      setHasInitialized(true);
    }
  }, [isOpen, timer, currentFolder, hasInitialized]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen]);

  const updateField = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Please enter a timer name.');
      return;
    }

    const finalConfig = { ...formData.config };

    if (formData.timer_type === 'countdown_duration') {
      finalConfig.durationSeconds =
        (formData.config.durationMinutes || 0) * 60 +
        (formData.config.durationSecondsInput || 0);

      if (finalConfig.durationSeconds <= 0) {
        setError('Please set a duration greater than 0.');
        return;
      }
    }

    if (formData.timer_type === 'countdown_datetime') {
      if (!formData.config.targetDate || !formData.config.targetTime) {
        setError('Please select a target date and time.');
        return;
      }
      const dt = new Date(`${formData.config.targetDate}T${formData.config.targetTime}`);
      finalConfig.targetTimestamp = Math.floor(dt.getTime() / 1000);

      if (finalConfig.targetTimestamp <= Date.now() / 1000) {
        setError('Target date/time must be in the future.');
        return;
      }
    }

    delete finalConfig.durationMinutes;
    delete finalConfig.durationSecondsInput;
    delete finalConfig.targetDate;
    delete finalConfig.targetTime;

    await onSave({
      name: formData.name,
      timer_type: formData.timer_type,
      folder_id: formData.folder_id,
      config: finalConfig
    });
    onClose();
  };

  const isValid = formData.name.trim() && (skipFolderSelect || formData.folder_id);

  const formatPreviewTime = () => {
    if (formData.timer_type === 'countup') return '00:00';
    if (formData.timer_type === 'countdown_duration') {
      const mins = formData.config.durationMinutes || 0;
      const secs = formData.config.durationSecondsInput || 0;
      if (formData.config.format === 'HH_MM_SS' || formData.config.showHours) {
        return `00:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      }
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    if (formData.timer_type === 'countdown_datetime') {
      return formData.config.format === 'D_HH_MM_SS' ? '2d 05:30:00' : '05:30:00';
    }
    return '00:00';
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#001233] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {timer ? 'Edit Timer' : 'Create Timer'}
                </h2>
                <p className="text-sm text-gray-400">Add a synchronized timer overlay</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {readOnly && (
              <div className="flex items-start gap-3 mx-6 mt-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                <p className="text-sm text-purple-200">
                  This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
                </p>
              </div>
            )}
            <div className="p-6 space-y-5">
              {/* Error Display */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 mb-2 block text-sm">Timer Name *</label>
                  <input
                    value={formData.name}
                    onChange={(e) => {
                      updateField('name', e.target.value);
                      setError('');
                    }}
                    placeholder="e.g., Break Timer, Offer Countdown"
                    disabled={readOnly}
                    className={`w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-cyan-500 transition-colors ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                </div>

                {!skipFolderSelect && (
                  <div>
                    <label className="text-gray-400 mb-2 block text-sm">Folder *</label>
                    <Select value={formData.folder_id} onValueChange={(v) => updateField('folder_id', v)} placeholder="Select folder...">
                      {folders?.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </Select>
                  </div>
                )}
              </div>

              {/* Timer Type Selection */}
              <div className="space-y-2">
                <label className="text-white font-medium text-sm">Timer Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {TIMER_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => updateField('timer_type', type.id)}
                      className={`p-3 rounded-xl text-left transition-all ${
                        formData.timer_type === type.id
                          ? 'bg-cyan-500/20 ring-2 ring-cyan-500'
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <type.icon className={`w-5 h-5 mb-1 ${
                        formData.timer_type === type.id ? 'text-cyan-400' : 'text-gray-400'
                      }`} />
                      <p className="font-medium text-white text-sm">{type.name}</p>
                      <p className="text-xs text-gray-400">{type.desc}</p>
                      <p className="text-xs text-cyan-400 mt-1 font-mono">{type.example}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Type-Specific Settings */}
              {formData.timer_type === 'countdown_duration' && (
                <div className="p-3 bg-white/5 rounded-xl space-y-3">
                  <label className="text-white font-medium text-sm">Duration</label>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        max={999}
                        value={formData.config.durationMinutes}
                        onChange={(e) => updateField('config.durationMinutes', parseInt(e.target.value) || 0)}
                        className="w-16 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-center outline-none focus:border-cyan-500"
                      />
                      <span className="text-gray-400 text-sm">min</span>
                    </div>
                    <span className="text-xl text-gray-500">:</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        max={59}
                        value={formData.config.durationSecondsInput}
                        onChange={(e) => updateField('config.durationSecondsInput', Math.min(59, parseInt(e.target.value) || 0))}
                        className="w-16 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-center outline-none focus:border-cyan-500"
                      />
                      <span className="text-gray-400 text-sm">sec</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.config.showHours}
                      onCheckedChange={(v) => updateField('config.showHours', v)}
                    />
                    <span className="text-xs text-gray-400">Show hours if duration is long</span>
                  </div>
                </div>
              )}

              {formData.timer_type === 'countdown_datetime' && (
                <div className="p-4 bg-white/5 rounded-xl space-y-4">
                  <label className="text-white font-medium text-sm">Target Date & Time</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Date</label>
                      <input
                        type="date"
                        value={formData.config.targetDate}
                        onChange={(e) => updateField('config.targetDate', e.target.value)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Time</label>
                      <input
                        type="time"
                        value={formData.config.targetTime}
                        onChange={(e) => updateField('config.targetTime', e.target.value)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-cyan-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                  </p>
                </div>
              )}

              {formData.timer_type === 'countup' && (
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-gray-400 text-sm">
                    Count-up timer starts at 00:00 and counts upward when activated.
                    Perfect for tracking segment duration or elapsed time.
                  </p>
                </div>
              )}

              {/* Display Settings */}
              <Tabs defaultValue="display" className="space-y-3">
                <TabsList className="bg-white/5 rounded-lg p-1">
                  <TabsTrigger value="display">Display</TabsTrigger>
                  <TabsTrigger value="style">Style</TabsTrigger>
                  <TabsTrigger value="behavior">Behavior</TabsTrigger>
                </TabsList>

                <TabsContent value="display" className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-white text-sm">Size</label>
                      <div className="grid grid-cols-3 gap-1">
                        {SIZE_OPTIONS.map((size) => (
                          <button
                            key={size.id}
                            type="button"
                            onClick={() => updateField('config.size', size.id)}
                            className={`p-2 rounded-lg text-center transition-all ${
                              formData.config.size === size.id
                                ? 'bg-cyan-500/20 ring-2 ring-cyan-500'
                                : 'bg-white/5 hover:bg-white/10'
                            }`}
                          >
                            <p className="text-xs font-medium text-white">{size.name}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-white text-sm">Position</label>
                      <Select value={formData.config.position} onValueChange={(v) => updateField('config.position', v)} placeholder="Select position...">
                        {POSITION_OPTIONS.map((pos) => (
                          <SelectItem key={pos.id} value={pos.id}>{pos.name}</SelectItem>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-white text-sm">Time Format</label>
                    <div className="grid grid-cols-3 gap-2">
                      {FORMAT_OPTIONS.map((fmt) => (
                        <button
                          key={fmt.id}
                          type="button"
                          onClick={() => updateField('config.format', fmt.id)}
                          className={`p-2 rounded-lg text-center transition-all ${
                            formData.config.format === fmt.id
                              ? 'bg-cyan-500/20 ring-2 ring-cyan-500'
                              : 'bg-white/5 hover:bg-white/10'
                          }`}
                        >
                          <p className="text-xs font-medium text-white">{fmt.name}</p>
                          <p className="text-[10px] text-gray-400 font-mono">{fmt.example}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                    <Switch
                      checked={formData.config.showLabel}
                      onCheckedChange={(v) => updateField('config.showLabel', v)}
                    />
                    <div className="flex-1">
                      {formData.config.showLabel ? (
                        <input
                          value={formData.config.labelText}
                          onChange={(e) => updateField('config.labelText', e.target.value)}
                          placeholder="Label text (e.g., Break ends in)"
                          className="w-full px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 outline-none focus:border-cyan-500"
                        />
                      ) : (
                        <span className="text-sm text-gray-400">Show label above timer</span>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="style" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-400 mb-2 block text-sm">Background Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={formData.config.theme.backgroundColor}
                          onChange={(e) => updateField('config.theme.backgroundColor', e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer border-0"
                        />
                        <input
                          value={formData.config.theme.backgroundColor}
                          onChange={(e) => updateField('config.theme.backgroundColor', e.target.value)}
                          className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-cyan-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-gray-400 mb-2 block text-sm">Text Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={formData.config.theme.textColor}
                          onChange={(e) => updateField('config.theme.textColor', e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer border-0"
                        />
                        <input
                          value={formData.config.theme.textColor}
                          onChange={(e) => updateField('config.theme.textColor', e.target.value)}
                          className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-cyan-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                    <div>
                      <p className="text-white text-sm">Transparent Background</p>
                      <p className="text-xs text-gray-400">Show only the timer text</p>
                    </div>
                    <Switch
                      checked={formData.config.theme.transparentBackground}
                      onCheckedChange={(v) => updateField('config.theme.transparentBackground', v)}
                    />
                  </div>

                  <div className="p-4 bg-black/30 rounded-xl">
                    <label className="text-gray-400 mb-3 block text-sm">Preview</label>
                    <div className="flex items-center justify-center py-8">
                      <TimerPreview config={formData.config} time={formatPreviewTime()} />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="behavior" className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Play className="w-4 h-4 text-cyan-400" />
                      <div>
                        <p className="text-white text-sm">Auto-start when shown</p>
                        <p className="text-xs text-gray-400">Timer begins immediately on stage</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.config.autoStartOnShow}
                      onCheckedChange={(v) => updateField('config.autoStartOnShow', v)}
                    />
                  </div>

                  {formData.timer_type === 'countdown_duration' && (
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Timer className="w-4 h-4 text-amber-400" />
                        <div>
                          <p className="text-white text-sm">Loop when finished</p>
                          <p className="text-xs text-gray-400">Restart timer at zero</p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.config.loopOnZero}
                        onCheckedChange={(v) => updateField('config.loopOnZero', v)}
                      />
                    </div>
                  )}

                  {formData.timer_type !== 'countup' && (
                    <div className="space-y-3">
                      <label className="text-white text-sm">Action when timer reaches zero</label>
                      <div className="grid grid-cols-1 gap-2">
                        {ACTION_ON_ZERO.map((action) => (
                          <button
                            key={action.id}
                            type="button"
                            onClick={() => updateField('config.actionOnZero', action.id)}
                            className={`p-3 rounded-xl text-left transition-all flex items-center gap-3 ${
                              formData.config.actionOnZero === action.id
                                ? 'bg-cyan-500/20 ring-2 ring-cyan-500'
                                : 'bg-white/5 hover:bg-white/10'
                            }`}
                          >
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              formData.config.actionOnZero === action.id
                                ? 'border-cyan-500 bg-cyan-500'
                                : 'border-gray-500'
                            }`}>
                              {formData.config.actionOnZero === action.id && (
                                <Check className="w-2.5 h-2.5 text-white" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{action.name}</p>
                              <p className="text-xs text-gray-400">{action.desc}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            {readOnly ? (onCopyToMyElements && (
              <button
                type="button"
                onClick={onCopyToMyElements}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Copy to My Elements
              </button>
            )) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={!isValid}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                {timer ? 'Save Changes' : 'Create Timer'}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
